// The Lead Inspector's list, on GET /admin/leads/inspector (backend:
// leads/inspector_api.py + leads/lead_inspector.py): recent leads in a
// range, or an exact search by lead id, email or phone across all dates.
//
// Every filter lives in the query string (q, status, origins, buyers, range,
// page), so a view can be shared and the back button walks through
// searches. Buyers and origins from a URL wait until the first response says
// which exist (`filter_options`); anything else - a hidden buyer's id
// included - is dropped before any request carries it.
import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Info, RefreshCw, SearchX } from 'lucide-react'
import api from '../../services/api'
import RangeSelect from '../staff-leads/RangeSelect'
import InspectorSearch from './InspectorSearch'
import StatusChips from './StatusChips'
import InspectorFilters from './InspectorFilters'
import LeadTable from './LeadTable'
import {
  FALLBACK_TIMEZONE, LIST_ENDPOINT, RANGE_PRESETS,
  allowedFilters, filtersToSearch, listParams, parseUrlFilters, tzLabel,
} from './model'

// Same shape as the staff dashboard's request state: `data` survives a
// refetch so the table never blanks, `key` says which params it answers.
const EMPTY = { data: null, key: null, loading: false, error: null }

function useListRequest(params, tick, enabled) {
  const [state, setState] = useState(EMPTY)
  const key = enabled ? JSON.stringify(params) : null

  useEffect(() => {
    if (!key) return undefined
    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null }))
    api.get(LIST_ENDPOINT, { params: JSON.parse(key) })
      .then(({ data }) => { if (!cancelled) setState({ data, key, loading: false, error: null }) })
      .catch((error) => { if (!cancelled) setState((s) => ({ ...s, loading: false, error })) })
    return () => { cancelled = true }
  }, [key, tick])

  return { ...state, current: state.key === key }
}

export default function InspectorList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const url = parseUrlFilters(searchParams)
  // The URL's buyers and origins, not yet checked against filter_options.
  // While set, requests leave them out and no rows are shown.
  const [pending, setPending] = useState(() => (
    url.buyers.length || url.origins.length ? { buyers: url.buyers, origins: url.origins } : null
  ))
  const [tick, setTick] = useState(0)

  const filters = pending ? { ...url, buyers: [], origins: [] } : url
  const list = useListRequest(listParams(filters), tick, true)

  const options = list.data?.filter_options
  useEffect(() => {
    if (!pending || !options) return
    const allowed = allowedFilters(pending, options)
    setPending(null)
    setSearchParams(filtersToSearch({ ...url, ...allowed }), { replace: true })
  }, [pending, options, url, setSearchParams])

  // Every change is a new history entry except paging and the clean-up of
  // a URL's filters, so Back returns to the previous search.
  const update = (patch, { resetPage = true } = {}) => {
    const next = { ...url, ...(resetPage ? { page: 1 } : {}), ...patch }
    setSearchParams(filtersToSearch(next))
  }
  const toggle = (key) => (value) => {
    const current = url[key]
    update({ [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value] })
  }

  const data = !pending && list.data ? list.data : null
  const timezone = data?.timezone || FALLBACK_TIMEZONE
  const searching = Boolean(url.q)
  const refresh = () => setTick((t) => t + 1)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header className="li-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>Lead Inspector</h1>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
            Everything recorded about a lead, from intake to postback · All times {tzLabel(timezone)}
          </p>
        </div>
        <div className="li-header-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          <div style={{ opacity: searching ? 0.5 : 1 }} title={searching ? 'A search covers all dates' : undefined}>
            <RangeSelect value={url.range} onChange={(range) => update({ range })} presets={RANGE_PRESETS} allowCustom />
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={refresh} disabled={list.loading} aria-label="Refresh">
            <RefreshCw size={14} className={list.loading ? 'li-spin' : undefined} aria-hidden="true" /> Refresh
          </button>
        </div>
      </header>

      <section className="card" aria-label="Search and filters" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <InspectorSearch value={url.q} onSearch={(q) => update({ q })} />
        {searching && (
          <Note>
            <strong style={{ fontWeight: 600 }}>Searching all dates</strong>
            {' '}for an exact match. The range doesn&apos;t apply to a search.
          </Note>
        )}
        {data && (
          <>
            <StatusChips counts={data.counts} active={url.status} onChange={(status) => update({ status })} />
            <InspectorFilters
              options={data.filter_options}
              origins={url.origins}
              buyers={url.buyers}
              onToggleOrigin={toggle('origins')}
              onToggleBuyer={toggle('buyers')}
              onClear={() => update({ origins: [], buyers: [] })}
            />
          </>
        )}
      </section>

      <Results list={list} data={data} url={url} timezone={timezone} onRetry={refresh} onPage={(page) => update({ page }, { resetPage: false })} />
    </div>
  )
}

function Results({ list, data, url, timezone, onRetry, onPage }) {
  if (!data && list.error) {
    return (
      <section className="card" role="alert" style={{ padding: 24, textAlign: 'center' }}>
        <p style={{ margin: '0 0 12px', color: 'var(--text-secondary)' }}>The leads could not be loaded.</p>
        <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>Retry</button>
      </section>
    )
  }
  if (!data) return <div className="li-skeleton" aria-busy="true" aria-label="Loading leads" style={{ height: 320 }} />

  const rows = data.rows || []
  const from = rows.length ? (data.page - 1) * data.page_size + 1 : 0
  const to = from ? from + rows.length - 1 : 0

  return (
    <section aria-label="Leads" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {list.error && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 12, padding: '8px 12px', borderRadius: 8, color: 'var(--red)', background: 'var(--red-bg)' }}>
          Could not refresh - showing the last results loaded.
          <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>Retry</button>
        </div>
      )}
      <div className="card" style={{ padding: 0, overflow: 'hidden', opacity: list.current ? 1 : 0.6, transition: 'opacity .15s' }}>
        {rows.length === 0 ? <EmptyState data={data} url={url} /> : <LeadTable rows={rows} timezone={timezone} />}
      </div>
      {rows.length > 0 && (
        <nav aria-label="Pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {from.toLocaleString('en-US')}–{to.toLocaleString('en-US')} of {Number(data.total).toLocaleString('en-US')}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.page <= 1} onClick={() => onPage(data.page - 1)}>Newer</button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={!data.has_next} onClick={() => onPage(data.page + 1)}>Older</button>
          </div>
        </nav>
      )}
    </section>
  )
}

function EmptyState({ data, url }) {
  let title = 'No leads match these filters'
  let hint = 'Try another status, origin or buyer, or a longer range.'
  if (data.search?.matched_by === 'none') {
    title = 'Not a lead ID, email or phone'
    hint = 'Search is exact: enter a full lead ID, a full email address or a 10-digit phone number.'
  } else if (url.q) {
    title = `No lead matches “${url.q}”`
    hint = url.status !== 'all' || url.origins.length || url.buyers.length
      ? 'Search is exact and still applies the status, origin and buyer filters - try clearing them.'
      : 'Search is exact: check the full lead ID, email or phone.'
  } else if (url.page > 1) {
    title = 'No more leads on this page'
    hint = 'Go back to newer leads.'
  }
  return (
    <div style={{ padding: '36px 20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <SearchX size={28} aria-hidden="true" style={{ color: 'var(--text-muted)' }} />
      <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{title}</p>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', maxWidth: 440 }}>{hint}</p>
    </div>
  )
}

function Note({ children }) {
  return (
    <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-secondary)', padding: '8px 12px', borderRadius: 8, background: 'var(--accent-soft)' }}>
      <Info size={14} aria-hidden="true" style={{ flexShrink: 0, color: 'var(--accent)' }} />
      <span>{children}</span>
    </p>
  )
}
